import { useCallback, useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import styles from './SaveBar.module.css';

/**
 * Immer sichtbare Speicher-Leiste (UX-Plan 2026-09-14, Phase 1.1).
 *
 * Ersetzt die früheren „Ungespeicherte Änderungen“-Klick-Divs, die nur nach einer
 * Änderung auftauchten (häufigstes Hilfe-Symptom: „Ich finde den Speichern-Knopf
 * nicht“). Die Leiste sitzt fest über dem Footer und zeigt genau einen von vier
 * Zuständen: gespeichert · ungespeichert · speichert · Fehler.
 *
 * `onSave` liefert `true`/`void` bei Erfolg, `false` oder eine Exception bei
 * Fehlschlag – dieselbe Konvention wie `saveCallbackRef` im UnsavedChangesContext.
 */
export interface SaveBarProps {
  dirty: boolean;
  onSave: () => Promise<boolean | void> | boolean | void;
  /** Optional: setzt die Änderungen auf den gespeicherten Stand zurück. */
  onDiscard?: () => void;
  saveLabel?: string;
  /** Kurzer Zusatztext links (z. B. „Studierende sehen deine Zeiten plus 30 Min“). */
  hint?: ReactNode;
  /** Slot rechts neben den Buttons (z. B. „Weiter“-Button oder Checkbox). */
  extra?: ReactNode;
  /** Fehlertext von außen (z. B. Server-Detail); überschreibt den generischen Text. */
  errorMessage?: string | null;
  /** Überschrift im Fehlerzustand (Standard „Speichern fehlgeschlagen“), z. B. bei Teilspeichern. */
  errorTitle?: string | null;
  disabled?: boolean;
}

function formatTime(d: Date): string {
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

export default function SaveBar({
  dirty,
  onSave,
  onDiscard,
  saveLabel = 'Speichern',
  hint,
  extra,
  errorMessage,
  errorTitle,
  disabled = false,
}: SaveBarProps) {
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const mounted = useRef(true);
  const barRef = useRef<HTMLDivElement | null>(null);

  // Die tatsächliche Höhe der Leiste (mobil wechselt sie mit Fehlertext oder `extra`)
  // als --savebar-h auf <body>: Platzhalter im Fluss und darüber schwebende Elemente
  // (Tages-Zusammenfassung der mobilen Zeiteingabe) richten sich danach.
  useEffect(() => {
    const el = barRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const apply = () => document.body.style.setProperty('--savebar-h', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(el);
    return () => {
      ro.disconnect();
      document.body.style.removeProperty('--savebar-h');
    };
  }, []);

  useEffect(() => {
    mounted.current = true;
    // Markiert die Seite für globales CSS (index.css): Toasts rücken über die Leiste.
    document.body.setAttribute('data-savebar', '1');
    return () => {
      mounted.current = false;
      document.body.removeAttribute('data-savebar');
    };
  }, []);

  // Ein neuer Fehler von außen (Server-Detail) zeigt den Fehlerzustand.
  useEffect(() => {
    if (errorMessage) setFailed(true);
  }, [errorMessage]);

  // Sobald nichts mehr ungespeichert ist, gilt der letzte Fehler als erledigt.
  useEffect(() => {
    if (!dirty) setFailed(false);
  }, [dirty]);

  const handleSave = useCallback(async () => {
    if (saving || disabled) return;
    setSaving(true);
    setFailed(false);
    try {
      const result = await onSave();
      if (!mounted.current) return;
      if (result === false) {
        setFailed(true);
      } else {
        setLastSavedAt(new Date());
      }
    } catch (err) {
      console.error('SaveBar: Speichern fehlgeschlagen', err);
      if (mounted.current) setFailed(true);
    } finally {
      if (mounted.current) setSaving(false);
    }
  }, [onSave, saving, disabled]);

  let state: 'saved' | 'dirty' | 'saving' | 'error';
  if (saving) state = 'saving';
  else if (failed) state = 'error';
  else if (dirty) state = 'dirty';
  else state = 'saved';

  const statusText = {
    saved: 'Alles gespeichert',
    dirty: 'Noch nicht gespeichert',
    saving: 'Wird gespeichert …',
    error: errorTitle || 'Speichern fehlgeschlagen',
  }[state];

  const subText =
    state === 'error'
      ? errorMessage || 'Bitte versuche es noch einmal.'
      : hint ?? (lastSavedAt ? `Zuletzt gespeichert ${formatTime(lastSavedAt)}` : null);

  return (
    <>
      <div className={styles.spacer} aria-hidden="true" />
      <div ref={barRef} className={`${styles.bar} ${styles[state]}`} role="status" data-testid="save-bar" data-state={state}>
        <div className={styles.status}>
          <span className={styles.dot} aria-hidden="true" />
          <strong className={styles.statusText}>{statusText}</strong>
          {subText && <span className={styles.subText}>{subText}</span>}
        </div>
        <div className={styles.actions}>
          {onDiscard && dirty && !saving && (
            <button type="button" className={styles.discardButton} onClick={onDiscard}>
              Verwerfen
            </button>
          )}
          <button
            type="button"
            className={styles.saveButton}
            onClick={handleSave}
            disabled={disabled || saving || (!dirty && state !== 'error')}
          >
            {state === 'error' ? 'Nochmal versuchen' : saveLabel}
          </button>
          {extra && <span className={styles.extra}>{extra}</span>}
        </div>
      </div>
    </>
  );
}
